import { Container, IconButton, Table, Tag, Text } from "@fluidattacks/design";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback } from "react";
import type { FormEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { NotCorrelatedCell } from "./styles";
import type { IHeaderTooltipProps, IThreatMatchProps } from "./types";

import type { IThreatMatchTableRow } from "../types";
import { useAudit } from "hooks/use-audit";
import { useTable } from "hooks/use-table";
import { useGroupUrl } from "pages/group/hooks";
import { severityFormatter } from "utils/format-helpers";
import { useTableServerSideStore } from "utils/tables";
import type { TStore } from "utils/zustand";

const MatchesTable: React.FC<IThreatMatchProps> = ({
  data,
  loading,
  fetchMore,
}: IThreatMatchProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { url: groupUrl } = useGroupUrl();
  const tableHelpers = useTableServerSideStore();
  const { groupName } = useParams<{ groupName: string }>();

  const notValidCell = <NotCorrelatedCell>{"-"}</NotCorrelatedCell>;

  const { addAuditEvent } = useAudit();

  const handleGoToFindingClick = useCallback(
    (row: Row<IThreatMatchTableRow>): FormEventHandler<HTMLElement> => {
      return (): void => {
        if (row.original.findingId === undefined) {
          return;
        }
        addAuditEvent("Matches.GoToFinding", row.original.findingId, groupName);
        navigate(`${groupUrl}/vulns/${row.original.findingId}/locations`);
      };
    },
    [addAuditEvent, groupName, groupUrl, navigate],
  );

  const formatVulnStatusesTags = (
    openVulns: number,
    closedVulns: number,
  ): JSX.Element => {
    return (
      <Container flexDirection={"row"}>
        <Tag
          priority={"low"}
          tagLabel={`Open=${openVulns}`}
          variant={"error"}
        />
        <Tag
          priority={"low"}
          tagLabel={`Closed=${closedVulns}`}
          variant={"success"}
        />
      </Container>
    );
  };

  const getColumns = (): ColumnDef<IThreatMatchTableRow>[] => {
    return [
      {
        accessorKey: "original",
        header: t("group.matches.matchesTable.identified"),
        id: "original",
        minSize: 300,
      },
      {
        accessorKey: "title",
        cell: (cell): JSX.Element =>
          cell.row.original.title === "" ? (
            <NotCorrelatedCell>
              {t("group.matches.matchesTable.notCorrelated")}
            </NotCorrelatedCell>
          ) : (
            <Container
              alignItems={"center"}
              display={"flex"}
              flexDirection={"row"}
              gap={0.25}
              pr={0.5}
              width={"100%"}
            >
              <Text color={theme.palette.gray[800]} size={"md"}>
                {cell.row.original.title}
              </Text>
              {cell.row.original.findingId === undefined ? undefined : (
                <IconButton
                  icon={"arrow-up-right-from-square"}
                  iconSize={"sm"}
                  iconType={"fa-light"}
                  onClick={handleGoToFindingClick(cell.row)}
                  variant={"ghost"}
                />
              )}
            </Container>
          ),
        header: t("group.matches.matchesTable.mapped"),
        id: "match",
        minSize: 200,
      },
      {
        accessorKey: "category",
        cell: (cell): string | JSX.Element =>
          cell.row.original.category ?? notValidCell,
        header: t("group.matches.matchesTable.category"),
      },
      {
        accessorFn: (row): number | null =>
          row.vulnType === 0 ? null : row.severityScoreV4,
        cell: (cell): JSX.Element | string =>
          cell.row.original.vulnType === 0
            ? notValidCell
            : severityFormatter(cell.row.original.severityScoreV4),
        header: t("group.matches.matchesTable.severity"),
        id: "severity",
        maxSize: 100,
      },
      {
        accessorFn: (row): number | null =>
          row.vulnType === 0 ? null : row.closedVulns + row.openVulns,
        cell: (cell): number | JSX.Element =>
          cell.row.original.vulnType === 0
            ? notValidCell
            : cell.row.original.closedVulns + cell.row.original.openVulns,
        header: t("group.matches.matchesTable.totalVulns"),
        maxSize: 150,
      },
      {
        accessorFn: (row): number | null =>
          row.vulnType === 0 ? null : row.openVulns,
        cell: (cell): JSX.Element | string =>
          cell.row.original.vulnType === 0
            ? notValidCell
            : formatVulnStatusesTags(
                cell.row.original.openVulns,
                cell.row.original.closedVulns,
              ),
        header: t("group.matches.matchesTable.status"),
        maxSize: 150,
      },
    ];
  };

  const tooltips: IHeaderTooltipProps[] = [
    {
      columnId: "original",
      tip: t("group.matches.matchesTable.tooltip"),
    },
  ];

  const { table } = useTable({
    columns: getColumns(),
    data,
    fetchMore,
    filters: tableHelpers as unknown as TStore<IThreatMatchTableRow, string>,
    id: "matches-table",
    options: {
      loading,
      paginationMode: "client-side",
      rowOptions: {
        getId: (row): string => row.original,
      },
      tooltips,
    },
    total: data.length,
  });

  return (
    <Container
      alignItems={"left"}
      display={"flex"}
      flexDirection={"column"}
      gap={0.25}
      width={"100%"}
    >
      <Text size={"md"} textAlign={"left"} wordBreak={"break-word"}>
        {t("group.matches.matchesTable.unmatchedInfo")}
      </Text>
      <Table table={table}>
        <Table.Content />
        <Table.Footer />
      </Table>
    </Container>
  );
};

export { MatchesTable };
